import type { DailyHistoryCell, EntityDailyHistoryRow } from './EntityDailyHistoryPanel.types';
import type { EntityDailyHistoryPanelProps } from './EntityDailyHistoryPanel.types';

const getDayKey = (value: Date): string => value.toISOString().slice(0, 10);

const buildEmptyDailyHistoryCell = (dayKey: string): DailyHistoryCell => ({
  day: new Date(`${dayKey}T00:00:00.000Z`),
  status: null,
  statuses: [],
  details: [],
});

const normalizeDailyHistoryCells = (
  dailyHistory: DailyHistoryCell[],
  dayKeys: string[],
): DailyHistoryCell[] => {
  if (dayKeys.length === 0) {
    return dailyHistory;
  }

  const cellsByDayKey = new Map(
    dailyHistory.map((historyCell) => [getDayKey(historyCell.day), historyCell] as const),
  );

  return dayKeys.map((dayKey) => cellsByDayKey.get(dayKey) ?? buildEmptyDailyHistoryCell(dayKey));
};

const collectRowDayKeys = (row: EntityDailyHistoryRow, collectedDayKeys: Set<string>): void => {
  row.dailyHistory.forEach((historyCell) => {
    collectedDayKeys.add(getDayKey(historyCell.day));
  });
  (row.childRows ?? []).forEach((childRow) => {
    collectRowDayKeys(childRow, collectedDayKeys);
  });
};

export const resolveEntityDailyHistoryDayKeys = (
  rows: EntityDailyHistoryRow[],
  explicitDayKeys?: string[],
): string[] => {
  if ((explicitDayKeys?.length ?? 0) > 0) {
    return explicitDayKeys ?? [];
  }

  const collectedDayKeys = new Set<string>();
  rows.forEach((row) => {
    collectRowDayKeys(row, collectedDayKeys);
  });

  return Array.from(collectedDayKeys).sort((left, right) => right.localeCompare(left));
};

export const normalizeEntityDailyHistoryRows = (
  rows: EntityDailyHistoryRow[],
  dayKeys: string[],
): EntityDailyHistoryRow[] =>
  rows.map((row) => ({
    ...row,
    dailyHistory: normalizeDailyHistoryCells(row.dailyHistory, dayKeys),
    childRows:
      row.childRows === undefined
        ? undefined
        : normalizeEntityDailyHistoryRows(row.childRows, dayKeys),
  }));

const buildSequentialIndices = (count: number): number[] =>
  Array.from({ length: count }, (_, index) => index);

export const resolveEntityDailyHistoryVisibleDayHeaderIndices = ({
  dayCount,
  gridWidthPx,
  minLabelWidthPx,
}: {
  dayCount: number;
  gridWidthPx: number;
  minLabelWidthPx: number;
}): number[] => {
  if (dayCount <= 0) {
    return [];
  }

  if (dayCount === 1 || gridWidthPx <= 0 || minLabelWidthPx <= 0) {
    return [0];
  }

  const maxVisibleLabelCount = Math.max(1, Math.floor(gridWidthPx / minLabelWidthPx));
  const visibleLabelCount = Math.min(dayCount, maxVisibleLabelCount);

  if (visibleLabelCount >= dayCount) {
    return buildSequentialIndices(dayCount);
  }

  const interval = Math.max(1, Math.ceil(dayCount / visibleLabelCount));
  const indices: number[] = [];

  for (let index = 0; index < dayCount; index += interval) {
    indices.push(index);
  }

  return indices;
};

type ResolveEntityDailyHistoryNameColumnWidthInput = Pick<
  EntityDailyHistoryPanelProps,
  'nameColumnMaxWidthRatio'
> & {
  dayCount: number;
  panelWidthPx: number;
  preferredWidthPx: number;
  minWidthPx: number;
  compressedMinWidthPx: number;
  statusBlockSizePx: number;
  statusColumnGapPx: number;
  statusCellHorizontalPaddingPx: number;
};

const resolveDenseWindowNameColumnCap = (
  dayCount: number,
  minWidthPx: number,
  _compressedMinWidthPx: number,
): number => {
  if (dayCount >= 28) return Math.max(minWidthPx, 420);
  if (dayCount >= 21) return Math.max(minWidthPx, 480);
  if (dayCount >= 14) return Math.max(minWidthPx, 560);
  return Number.POSITIVE_INFINITY;
};

export const resolveEntityDailyHistoryNameColumnWidth = ({
  dayCount,
  panelWidthPx,
  preferredWidthPx,
  minWidthPx,
  compressedMinWidthPx,
  nameColumnMaxWidthRatio,
  statusBlockSizePx,
  statusColumnGapPx,
  statusCellHorizontalPaddingPx,
}: ResolveEntityDailyHistoryNameColumnWidthInput): number => {
  if (panelWidthPx <= 0 || !Number.isFinite(panelWidthPx)) {
    return Math.max(preferredWidthPx, minWidthPx);
  }

  const minimumStatusWidthPx =
    dayCount <= 0
      ? 0
      : dayCount * statusBlockSizePx +
        Math.max(0, dayCount - 1) * statusColumnGapPx +
        statusCellHorizontalPaddingPx;
  const availableNameWidthPx = Math.max(0, panelWidthPx - minimumStatusWidthPx);
  const effectiveMinWidthPx = Math.min(
    minWidthPx,
    Math.max(compressedMinWidthPx, availableNameWidthPx),
  );
  const ratioMaxWidthPx = Math.floor(panelWidthPx * (nameColumnMaxWidthRatio ?? 0.38));
  const denseWindowMaxWidthPx = resolveDenseWindowNameColumnCap(
    dayCount,
    minWidthPx,
    compressedMinWidthPx,
  );
  const effectiveMaxWidthPx = Math.max(
    effectiveMinWidthPx,
    Math.min(ratioMaxWidthPx, availableNameWidthPx || ratioMaxWidthPx, denseWindowMaxWidthPx),
  );

  return Math.min(Math.max(preferredWidthPx, effectiveMinWidthPx), effectiveMaxWidthPx);
};
